import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Loader2, Save, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import CitedContent from "@/components/taxonomy/CitedContent";
import ConfirmModal from "@/components/common/ConfirmModal";
import { useConfirmModal } from "@/hooks/useConfirmModal";

interface Aspect {
  id: string;
  kode: string;
  nama: string;
  urutan: number;
  is_active: boolean;
}

interface NodeContent {
  id: string;
  taxonomy_node_id: string;
  aspect_id: string;
  aspect_kode: string;
  aspect_nama: string;
  title: string | null;
  content_html: string | null;
  status: "DRAFT" | "PUBLISHED" | "NONAKTIF";
  urutan: number;
  published_at: string | null;
  updated_at: string | null;
}

interface Props {
  nodeId: string;
  aspect: Aspect;
  /** Konten tersimpan untuk aspek ini (jika ada). */
  content?: NodeContent;
  /** Sembunyikan sub-judul (mis. bila section hanya berisi 1 aspek & tab
   * section sudah menampilkan namanya). */
  hideHeader?: boolean;
}

/**
 * Editor mandiri untuk SATU aspek taxonomy (judul + status + rich text).
 * Mengelola draft, simpan (PUT), dan hapus (DELETE) sendiri sehingga beberapa
 * blok bisa ditumpuk dalam satu "section" tanpa berbagi state.
 */
export default function AspectEditorBlock({
  nodeId,
  aspect,
  content,
  hideHeader,
}: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{
    title: string;
    content_html: string;
    status: NodeContent["status"];
  }>({
    title: content?.title ?? "",
    content_html: content?.content_html ?? "",
    status: content?.status ?? "DRAFT",
  });
  const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");
  const { modalProps, confirm } = useConfirmModal();

  // Sinkronkan draft saat konten tersimpan berubah (mis. diisi AI, atau node
  // berganti). Key pada id + updated_at agar poll yang tidak mengubah konten
  // tidak menimpa editan user yang belum disimpan.
  useEffect(() => {
    setDraft({
      title: content?.title ?? "",
      content_html: content?.content_html ?? "",
      status: content?.status ?? "DRAFT",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id, content?.updated_at]);

  const upsertMutation = useMutation({
    mutationFn: async () =>
      api.put<NodeContent>(`/taxonomy/${nodeId}/contents`, {
        aspect_kode: aspect.kode,
        title: draft.title || null,
        content_html: draft.content_html || null,
        status: draft.status,
        urutan: aspect.urutan,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taxonomy-contents", nodeId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api.delete(`/taxonomy/${nodeId}/contents/${aspect.kode}`),
    onSuccess: () => {
      setDraft({ title: "", content_html: "", status: "DRAFT" });
      queryClient.invalidateQueries({ queryKey: ["taxonomy-contents", nodeId] });
    },
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
      {!hideHeader && (
        <h3 className="mb-3 text-sm font-semibold text-gray-800">{aspect.nama}</h3>
      )}

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Judul (opsional)"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          <select
            value={draft.status}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                status: e.target.value as NodeContent["status"],
              }))
            }
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="DRAFT">DRAFT</option>
            <option value="PUBLISHED">PUBLISHED</option>
            <option value="NONAKTIF">NONAKTIF</option>
          </select>
        </div>

        {/* Toggle Edit / Pratinjau. Pratinjau membuat sitasi dapat diklik
            (membuka dokumen sumber); editor Quill tidak mempertahankan
            atribut sitasi bila konten disimpan ulang. */}
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-0.5 w-fit text-xs">
          {(["edit", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setEditorMode(m)}
              className={clsx(
                "rounded-md px-3 py-1 font-medium transition-colors",
                editorMode === m
                  ? "bg-gray-100 text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              {m === "edit" ? "Edit" : "Pratinjau"}
            </button>
          ))}
        </div>

        {editorMode === "edit" ? (
          <RichTextEditor
            value={draft.content_html}
            onChange={(html) => setDraft((d) => ({ ...d, content_html: html }))}
            placeholder="Tulis konten…"
          />
        ) : draft.content_html ? (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <CitedContent variant="admin" html={draft.content_html} />
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-400">
            Belum ada konten untuk dipratinjau.
          </p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {content?.updated_at
              ? `Terakhir diperbarui ${new Date(content.updated_at).toLocaleString("id-ID")}`
              : "Belum tersimpan"}
          </p>
          <div className="flex items-center gap-2">
            {content && (
              <button
                type="button"
                disabled={deleteMutation.isPending || upsertMutation.isPending}
                onClick={() =>
                  confirm({
                    title: "Hapus konten aspek",
                    message:
                      "Konten aspek ini akan dihapus permanen dan tidak dapat dibatalkan. Lanjutkan?",
                    confirmLabel: "Hapus",
                    variant: "danger",
                    onConfirm: () => deleteMutation.mutate(),
                  })
                }
                title="Hapus konten aspek ini"
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Hapus
              </button>
            )}
            <button
              type="button"
              disabled={upsertMutation.isPending}
              onClick={() => upsertMutation.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400"
            >
              {upsertMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Simpan
            </button>
          </div>
        </div>
        {upsertMutation.isError && (
          <p className="text-xs text-red-600">
            Gagal menyimpan. Periksa koneksi & coba lagi.
          </p>
        )}
        {deleteMutation.isError && (
          <p className="text-xs text-red-600">Gagal menghapus konten. Coba lagi.</p>
        )}
      </div>

      <ConfirmModal {...modalProps} loading={deleteMutation.isPending} />
    </div>
  );
}
